import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCategory,
  deleteCategory,
  fetchAllCategories,
  fetchCategory,
  searchCategories,
  updateCategory,
} from '~/api/category';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import { captureException } from '~/hooks/useExceptionLogger';
import { CashFlowQueryKeys } from '~/types/cashflow';
import { Category, CategoryCreate, CategoryQueryKeys } from '~/types/category';
import { DocumentQueryKeys } from '~/types/document';
import { FlowsQueryKeys } from '~/types/flow';
import { RecurrenceQueryKeys } from '~/types/recurrence';
import { TransactionsKeys } from '~/types/transaction';
import { AnalyticsEvents } from './analytics';

export const useCategoriesQuery = () => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: CategoryQueryKeys.company(companyId),
    queryFn: () => fetchAllCategories(companyId),
    enabled: !!companyId,
    staleTime: 30000,
  });
};

const hasOnly1Category = async (companyId: string) => {
  try {
    const resp = await searchCategories(
      companyId,
      {
        search: '',
      },
      { size: 2 },
    );
    return resp.data.length === 1;
  } catch (err) {
    captureException(err, {
      extra: {
        help_text: 'error checking if there is only 1 category',
      },
    });
    return false;
  }
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: (params: Omit<CategoryCreate, 'company'>) => createCategory({ ...params, company: { id: companyId } }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: CategoryQueryKeys.company(companyId) });
      track(AnalyticsEvents.CATEGORY_CREATED);
      if (await hasOnly1Category(companyId)) track(AnalyticsEvents.FIRST_CATEGORY_ASSIGNED);
    },
    onError: (error, { name, color }) => {
      captureException(error, {
        extra: {
          category_name: name,
          category_color: color,
        },
      });
    },
  });
};

export const useUpdateCategory = ({ currentName }: { currentName: string }) => {
  const queryClient = useQueryClient();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: updateCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CategoryQueryKeys.company(companyId) });
      queryClient.invalidateQueries({ queryKey: CashFlowQueryKeys.tableByCompany(companyId) });
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
    },
    onError: (error, { id, update }) => {
      captureException(error, {
        extra: {
          category_id: id,
          current_category_name: currentName,
          new_category_name: update.name,
        },
      });
    },
  });
};

export const useCategoryQuery = (id: Category['id'] | null) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: CategoryQueryKeys.single(companyId, id || ''),
    queryFn: () => fetchCategory(id || ''),
    enabled: Boolean(id) && Boolean(companyId),
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  const companyId = useActiveCompanyId();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CategoryQueryKeys.company(companyId) });
      queryClient.invalidateQueries({ queryKey: CashFlowQueryKeys.tableByCompany(companyId) });
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: FlowsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: RecurrenceQueryKeys.all });
    },
    onSettled: () => track(AnalyticsEvents.CATEGORY_DELETED),
  });
};
